import React, { FC, useEffect, useRef, useState } from 'react'
import { LabelProps, TagProps, TagListProps } from '@sport1/types/web'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { useRouter } from 'next/router'
import { isFontelloIcon } from '@sport1/news-styleguide/FontelloHelpers'
import CardHeadline from '@sport1/news-styleguide/CardHeadline'
import WrappedNavigationListItem from '../WrappedNavigationListItem'
import nofollowExternal from '@/utils/url/nofollowExternal'
import useInViewport from '@/hooks/useInViewport'

type Props = Pick<TagListProps, 'headline' | 'content'> & {
    bold?: boolean
    onTagPress?: (tag: TagProps | LabelProps) => void
}
const LazyTagListItem: FC<{
    tag: TagProps | LabelProps
    index: number
    onPress?: (tag: TagProps | LabelProps) => void
    asPath: string
}> = ({ tag, index, onPress, asPath }) => {
    const ref = useRef<HTMLDivElement | null>(null)
    const isVisible = useInViewport(ref)

    const href = tag.href || ''

    const [hasBeenVisible, setHasBeenVisible] = useState(false)

    useEffect(() => {
        if (isVisible && !hasBeenVisible) setHasBeenVisible(true)
    }, [isVisible, hasBeenVisible])

    return (
        <div
            ref={ref}
            style={{
                minHeight: '48px',
                width: '100%',
            }}
        >
            {hasBeenVisible ? (
                <WrappedNavigationListItem
                    testID={`tag-list-navigation-item-${tag.title}`}
                    title={tag.title}
                    value={tag.childrenCount}
                    leadingType={tag.iconName && isFontelloIcon(tag.iconName) ? 'icon' : 'image'}
                    leading={
                        tag.iconName && isFontelloIcon(tag.iconName) ? tag.iconName : tag.iconUrl
                    }
                    alt={tag.title}
                    backgroundColor={href === asPath ? 'onyx-opacity-12' : 'pearl'}
                    trailingIcon="arrow_forward_ios"
                    trailingIconColor="keshi-3"
                    href={href}
                    onPress={onPress ? () => onPress(tag) : undefined}
                    webRel={nofollowExternal}
                    webTarget={tag.hrefTarget}
                    marginTop={index > 0 ? 'spacing-1' : undefined}
                />
            ) : (
                <div
                    style={{
                        width: '100%',
                        height: '48px',
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                />
            )}
        </div>
    )
}

const TagList: FC<Props> = ({ headline, content, onTagPress }) => {
    const { asPath } = useRouter()

    return (
        <NonFlexingContainer>
            {headline && (
                <NonFlexingContainer paddingX="spacing-6" paddingBottom="spacing-6">
                    <CardHeadline cardHeadlineType="cardHeadlineXS" text={headline} />
                </NonFlexingContainer>
            )}

            {content.map((tag, index) => (
                <LazyTagListItem
                    key={`${tag.id || tag.contextId}_${index}`}
                    tag={tag}
                    index={index}
                    asPath={asPath}
                    onPress={onTagPress}
                />
            ))}
        </NonFlexingContainer>
    )
}

export default React.memo(TagList)
